import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Bell, BellOff, Calendar, Check, Download, ExternalLink, FileText,
  History, Layers, Loader2, MessageSquare, Pencil, Send, Star, Trash2, X,
} from "lucide-react";

import { DocumentFormDialog } from "@/components/DocumentFormDialog";
import { useConfirm } from "@/components/confirm-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useDocument } from "@/hooks/useDetail";
import { useDeleteDocument } from "@/hooks/useDocumentMutations";
import {
  useCreateDocumentComment,
  useDeleteDocumentComment,
  useDocumentComments,
  useDocumentVersions,
  useSubscribeDocument,
  useUnsubscribeDocument,
  useUpdateDocumentComment,
} from "@/hooks/useDocumentExtras";
import { FILE_TYPE_STYLES } from "@/lib/file-icons";
import { formatBytes, formatDate, formatDateTime, initials } from "@/lib/format";
import type { DocumentCommentRow, DocumentVersionRow, WorkflowState } from "@/lib/types";
import { cn } from "@/lib/utils";

/** /library/:id — full document view: hero, description (EN + AM),
 *  metadata sidebar, tags, owner card, manage actions. */
export default function DocumentDetail() {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const id = params.id ? params.id : undefined;
  const { data: user } = useCurrentUser();
  const { data: doc, isLoading, error } = useDocument(id);
  const del         = useDeleteDocument();
  const subscribe   = useSubscribeDocument();
  const unsubscribe = useUnsubscribeDocument();
  const confirm     = useConfirm();
  const [editing, setEditing] = useState(false);

  if (isLoading) return <DetailSkeleton />;
  if (error || !doc) return <NotFound back="/library" label="library" />;

  const style = FILE_TYPE_STYLES[doc.file_type];
  const Icon = style.icon;

  const canManage = !!user && (
    user.is_director_level
    || (doc.owner_id === user.id && doc.workflow_state !== "published")
  );

  async function onDelete() {
    if (!doc) return;
    const ok = await confirm({
      title: `Delete "${doc.title}"?`,
      description: "This cannot be undone.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    del.mutate(doc.id, { onSuccess: () => navigate("/library") });
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <DocumentFormDialog
        open={editing}
        onOpenChange={setEditing}
        document={doc}
      />

      {/* ---------- Back link + actions ---------- */}
      <div className="flex items-center justify-between">
        <Button asChild variant="ghost" size="sm" className="-ml-3">
          <Link to="/library">
            <ArrowLeft className="mr-2 h-4 w-4" />Back to library
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant={doc.subscribed ? "secondary" : "outline"}
            size="sm"
            disabled={subscribe.isPending || unsubscribe.isPending}
            onClick={() =>
              doc.subscribed
                ? unsubscribe.mutate(doc.id)
                : subscribe.mutate(doc.id)
            }
          >
            {subscribe.isPending || unsubscribe.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : doc.subscribed ? (
              <BellOff className="mr-2 h-4 w-4" />
            ) : (
              <Bell className="mr-2 h-4 w-4" />
            )}
            {doc.subscribed ? "Unsubscribe" : "Subscribe"}
          </Button>
          {canManage && (
            <>
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                <Pencil className="mr-2 h-4 w-4" />Edit
              </Button>
              <Button
                variant="outline" size="sm"
                className="border-rose-200 dark:border-rose-500/30 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:bg-rose-500/15 hover:text-rose-800 dark:text-rose-300"
                disabled={del.isPending}
                onClick={onDelete}
              >
                {del.isPending
                  ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <Trash2 className="mr-2 h-4 w-4" />}
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ---------- Hero card ---------- */}
      <Card className="overflow-hidden border-border/60 shadow-soft">
        <div className="flex items-start gap-5 p-6 md:p-8">
          <div className={cn(
            "flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl shadow-soft",
            style.chipClass,
          )}>
            <Icon className="h-7 w-7" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              {doc.is_featured && (
                <Badge variant="outline" className="border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300">
                  <Star className="mr-1 h-3 w-3 fill-amber-400" />Featured
                </Badge>
              )}
              <WorkflowBadge state={doc.workflow_state} label={doc.workflow_state_display} />
              <Badge variant="outline" className={cn("font-mono uppercase", style.chipClass)}>
                {doc.file_extension || doc.file_type}
              </Badge>
              {doc.file_count > 1 && (
                <Badge
                  variant="outline"
                  className="border-primary/30 bg-primary/10 text-primary"
                  title={`This resource bundles ${doc.file_count} files`}
                >
                  <Layers className="mr-1 h-3 w-3" />
                  {doc.file_count} files
                </Badge>
              )}
            </div>
            <h1 className="mt-2 text-2xl font-bold tracking-tight md:text-3xl">{doc.title}</h1>
            {/* Quick actions on the primary (first) attachment. The full
                multi-file list lives in the FilesCard below. */}
            {doc.file_url && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Button asChild>
                  <a href={doc.file_url} target="_blank" rel="noreferrer">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Open {doc.files && doc.files.length > 1 ? "primary file" : "file"}
                  </a>
                </Button>
                <Button asChild variant="outline">
                  <a href={doc.file_url} download>
                    <Download className="mr-2 h-4 w-4" />Download
                  </a>
                </Button>
                {doc.files && doc.files.length > 1 && (
                  <span className="text-xs text-muted-foreground">
                    +{doc.files.length - 1} more {doc.files.length - 1 === 1 ? "file" : "files"} below
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* ---------- Body + sidebar ---------- */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {doc.description && (
            <Card className="border-border/60 shadow-soft">
              <CardHeader>
                <CardTitle className="text-base">Description</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 text-sm leading-relaxed">
                <p className="whitespace-pre-wrap text-foreground">{doc.description}</p>
              </CardContent>
            </Card>
          )}

          {doc.files && doc.files.length > 0 && (
            <Card className="border-border/60 shadow-soft">
              <CardHeader>
                <CardTitle className="text-base">
                  Files
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    {doc.files.length} file{doc.files.length === 1 ? "" : "s"} in this resource
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <ul className="space-y-2">
                  {doc.files.map((f, i) => (
                    <li
                      key={f.id}
                      className="flex items-center gap-3 rounded-md border bg-muted/20 p-2.5"
                    >
                      <FileText className="h-5 w-5 shrink-0 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium">{f.original_name}</span>
                          {i === 0 && (
                            <span className="rounded bg-primary/10 px-1.5 py-px text-[10px] font-medium text-primary">
                              Primary
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatBytes(f.size_bytes)}
                          {f.content_type ? ` · ${f.content_type}` : ""}
                        </div>
                      </div>
                      {f.file_url && (
                        <>
                          <Button asChild variant="ghost" size="sm">
                            <a href={f.file_url} target="_blank" rel="noreferrer">
                              <ExternalLink className="h-3.5 w-3.5" />
                              <span className="sr-only">Open</span>
                            </a>
                          </Button>
                          <Button asChild variant="outline" size="sm">
                            <a href={f.file_url} download>
                              <Download className="mr-1.5 h-3.5 w-3.5" />
                              Download
                            </a>
                          </Button>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {doc.tags.length > 0 && (
            <Card className="border-border/60 shadow-soft">
              <CardHeader><CardTitle className="text-base">Tags</CardTitle></CardHeader>
              <CardContent className="flex flex-wrap gap-2 pt-0">
                {doc.tags.map((t) => (
                  <Badge key={t.id} variant="secondary" className="font-medium">{t.name}</Badge>
                ))}
              </CardContent>
            </Card>
          )}

          <CommentsSection documentId={doc.id} initialCount={doc.comment_count} />
        </div>

        <div className="space-y-6">
          <Card className="h-fit border-border/60 shadow-soft">
            <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
            <CardContent className="space-y-3 pt-0 text-sm">
              <MetaRow label="Category" value={doc.category_display} />
              <MetaRow label="Type"     value={doc.file_type_display} />
              <MetaRow label="Size"     value={formatBytes(doc.file_size_bytes)} mono />
              <MetaRow label="Downloads" value={doc.download_count.toLocaleString()} mono />
              <MetaRow label="Owner"    value={doc.owner_name || "—"} />
              <MetaRow
                label="Created"
                value={formatDateTime(doc.created_at)}
                icon={<Calendar className="h-3 w-3" />}
              />
              <MetaRow label="Updated" value={formatDateTime(doc.updated_at)} />
              {doc.expires_at && <MetaRow label="Expires" value={formatDate(doc.expires_at)} />}
            </CardContent>
          </Card>

          <VersionsCard documentId={doc.id} count={doc.version_count} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared bits (also used by ArticleDetail + EventDetail).
// ---------------------------------------------------------------------------

export function MetaRow({
  label, value, mono = false, icon,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  icon?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border/40 pb-2 last:border-b-0 last:pb-0">
      <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className={cn(
        "flex items-center gap-1.5 text-right text-sm font-medium",
        mono && "font-mono tabular-nums",
      )}>
        {icon}{value}
      </span>
    </div>
  );
}

export function WorkflowBadge({ state, label }: { state: WorkflowState; label: string }) {
  const className =
    state === "published"          ? "bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30"
  : state === "pending_review"     ? "bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/30"
  : state === "pending_ceo_review" ? "bg-violet-50 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-500/30"
  : state === "rejected"           ? "bg-rose-50 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-500/30"
  : state === "private_only"       ? "bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-500/30"
                                   : "bg-muted/40 text-foreground/80 border-border";
  return <Badge variant="outline" className={cn("border font-medium", className)}>{label}</Badge>;
}

// ---------------------------------------------------------------------------
// Comments — threaded list + composer
// ---------------------------------------------------------------------------

function CommentsSection({ documentId, initialCount }: { documentId: string; initialCount: number }) {
  const { data: user } = useCurrentUser();
  const list = useDocumentComments(documentId);
  const create = useCreateDocumentComment();
  const [draft, setDraft] = useState("");

  const visibleCount = list.data ? list.data.count : initialCount;
  const canSubmit = draft.trim().length > 0 && !create.isPending && !!user;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    create.mutate(
      { document: documentId, body: draft.trim() },
      { onSuccess: () => setDraft("") },
    );
  }

  return (
    <Card className="border-border/60 shadow-soft">
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
          Comments
          <Badge variant="outline" className="font-mono tabular-nums">{visibleCount}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        {list.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : list.data && list.data.results.length > 0 ? (
          <ul className="space-y-3">
            {list.data.results.map((c) => (
              <li key={c.id}>
                <CommentRow comment={c} currentUserId={user?.id ?? null} canMod={!!user?.is_director_level} />
              </li>
            ))}
          </ul>
        ) : (
          <p className="rounded-md border border-dashed py-6 text-center text-sm text-muted-foreground">
            No comments yet. Start the conversation.
          </p>
        )}

        {user && (
          <form onSubmit={onSubmit} className="space-y-2 border-t border-border/60 pt-4">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Add a comment…"
              rows={3}
              maxLength={4000}
            />
            <div className="flex justify-end">
              <Button type="submit" size="sm" disabled={!canSubmit}>
                {create.isPending
                  ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Posting…</>
                  : <><Send className="mr-2 h-4 w-4" />Comment</>}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

function CommentRow({
  comment, currentUserId, canMod,
}: {
  comment: DocumentCommentRow;
  currentUserId: string | null;
  canMod: boolean;
}) {
  const update  = useUpdateDocumentComment();
  const del     = useDeleteDocumentComment();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(comment.body);

  const isAuthor = currentUserId != null && comment.author_id === currentUserId;
  const canDel   = isAuthor || canMod;

  function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.trim()) return;
    update.mutate(
      { id: comment.id, document: comment.document, body: draft.trim() },
      { onSuccess: () => setEditing(false) },
    );
  }

  async function onDelete() {
    const ok = await confirm({
      title: "Delete this comment?",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    del.mutate({ id: comment.id, document: comment.document });
  }

  return (
    <div id={`comment-${comment.id}`} className="flex items-start gap-3 rounded-md border border-border/60 bg-card p-3">
      <Avatar className="h-8 w-8 ring-1 ring-border">
        {comment.author?.avatar_url && <AvatarImage src={comment.author.avatar_url} alt="" />}
        <AvatarFallback className="bg-gradient-to-br from-primary to-emerald-700 text-[11px] font-semibold text-primary-foreground">
          {initials(comment.author_name || "")}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">{comment.author_name || "—"}</span>
            {" · "}{formatDateTime(comment.created_at)}
            {comment.edited_at && <span className="ml-1">(edited)</span>}
          </div>
          {!editing && (
            <div className="flex items-center gap-1">
              {comment.can_edit && (
                <Button variant="ghost" size="icon" className="h-7 w-7"
                  onClick={() => { setDraft(comment.body); setEditing(true); }}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )}
              {canDel && (
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
                  disabled={del.isPending} onClick={onDelete}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          )}
        </div>
        {editing ? (
          <form onSubmit={onSave} className="mt-2 space-y-2">
            <Textarea rows={3} value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={4000} />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)}>
                <X className="mr-1 h-3.5 w-3.5" />Cancel
              </Button>
              <Button type="submit" size="sm" disabled={!draft.trim() || update.isPending}>
                {update.isPending
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Check className="mr-1 h-3.5 w-3.5" />}
                Save
              </Button>
            </div>
          </form>
        ) : (
          <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed">{comment.body}</p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Versions — read-only history of file replacements
// ---------------------------------------------------------------------------

function VersionsCard({ documentId, count }: { documentId: string; count: number }) {
  // Only fetch when there's something to fetch — keeps the request count
  // down on docs that have never been re-uploaded.
  const enabled = count > 0;
  const list = useDocumentVersions(enabled ? documentId : undefined);

  if (!enabled) return null;

  return (
    <Card className="border-border/60 shadow-soft">
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <History className="h-4 w-4 text-muted-foreground" />
          Version history
          <Badge variant="outline" className="font-mono tabular-nums">{count}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {list.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : list.data && list.data.length > 0 ? (
          <ul className="divide-y divide-border/40">
            {list.data.map((v) => <VersionRow key={v.id} version={v} />)}
          </ul>
        ) : (
          <p className="text-xs text-muted-foreground">No prior versions on record.</p>
        )}
      </CardContent>
    </Card>
  );
}

function VersionRow({ version }: { version: DocumentVersionRow }) {
  return (
    <li className="py-2 first:pt-0 last:pb-0">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium">v{version.version_number}</div>
          <div className="text-[11px] text-muted-foreground">
            {formatDate(version.uploaded_at)}
            {version.uploaded_by_name && ` · ${version.uploaded_by_name}`}
            {" · "}{formatBytes(version.file_size_bytes)}
          </div>
          {version.notes && (
            <div className="mt-1 text-xs text-muted-foreground">{version.notes}</div>
          )}
        </div>
        {version.file_url && (
          <Button asChild variant="ghost" size="icon" className="h-7 w-7" title="Download this version">
            <a href={version.file_url} target="_blank" rel="noreferrer">
              <Download className="h-3.5 w-3.5" />
            </a>
          </Button>
        )}
      </div>
    </li>
  );
}

function DetailSkeleton() {
  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <Skeleton className="h-8 w-32" />
      <Skeleton className="h-32 w-full" />
      <div className="grid gap-6 lg:grid-cols-3">
        <Skeleton className="h-64 w-full lg:col-span-2" />
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  );
}

export function NotFound({ back, label }: { back: string; label: string }) {
  return (
    <div className="mx-auto max-w-md py-20 text-center">
      <h2 className="text-lg font-semibold">Not found</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        We couldn't load this item. It may have been deleted, or you don't have access.
      </p>
      <Button asChild variant="outline" className="mt-4">
        <Link to={back}>
          <ArrowLeft className="mr-2 h-4 w-4" />Back to {label}
        </Link>
      </Button>
    </div>
  );
}
